import { StandardEvents } from '@shopify/events';

const config = document.getElementById('ev-mobile-choice');
const sticky = document.querySelector('sticky-add-to-cart[data-fabric-review]');
const section = sticky?.closest('.shopify-section');
const mobile = matchMedia('(max-width: 749px)');
const choices = new Map();
let pending = false;
let revision = 0;
let prompted = false;
let autoSelectOption = null;
let automaticChange = false;
const picker = () => section?.querySelector('variant-picker');
const groups = () => [...(picker()?.querySelectorAll('fieldset') || [])];
let variant = JSON.parse(picker()?.querySelector('script[type="application/json"]')?.textContent || 'null');
const missing = () => groups().filter(group => !choices.has(group.dataset.fieldsetIndex));

function updateAvailability() {
  const colourGroup = groups().find(group => /^(color|colour|couleur)$/i.test(group.querySelector('input')?.dataset.optionName || ''));
  const colourChosen = !mobile.matches || !colourGroup || choices.has(colourGroup.dataset.fieldsetIndex);
  for (const group of groups()) {
    for (const input of group.querySelectorAll('input[type="radio"]')) {
      const isColour = /^(color|colour|couleur)$/i.test(input.dataset.optionName || '');
      const available = isColour || !colourChosen ? input.dataset.anyAvailable : input.dataset.optionAvailable;
      input.closest('label').hidden = available === 'false';
    }
  }
}

function sync() {
  if (!section || !sticky) return;
  section.classList.toggle('ev-mobile-choice', mobile.matches);
  if (!mobile.matches) { updateAvailability(); return; }
  for (const group of groups()) {
    const chosen = choices.get(group.dataset.fieldsetIndex);
    for (const input of group.querySelectorAll('input[type="radio"]')) {
      input.checked = input.value === chosen;
      input.dataset.currentChecked = String(input.checked);
      input.dataset.previousChecked = 'false';
      if (!choices.has('0')) {
        input.removeAttribute('aria-disabled');
        input.setAttribute('aria-label', input.value);
        input.dataset.optionAvailable = 'true';
      }
    }
    const value = group.querySelector('.variant-option__swatch-value');
    if (value) value.textContent = chosen || '';
  }
  updateAvailability();
  const incomplete = missing().length > 0;
  const button = sticky.querySelector('[ref="addToCartButton"]');
  const title = sticky.querySelector('.sticky-add-to-cart__variant');
  sticky.dataset.needsChoice = String(incomplete);
  sticky.dataset.choiceReady = 'true';
  if (incomplete) {
    if (title) title.textContent = config.dataset.choose;
    if (button) {
      button.disabled = false;
      const label = button.querySelector('.add-to-cart-text__content');
      if (label?.firstElementChild) label.firstElementChild.textContent = config.dataset.add;
    }
  } else if (button) {
    button.disabled = pending || !variant?.available;
  }
  const id = section.querySelector('product-form-component input[name="id"]');
  if (id) id.value = incomplete || pending ? '' : String(variant?.id || '');
  let message = picker()?.querySelector('.ev-choice-message');
  if (prompted && !message) {
    message = document.createElement('p');
    message.className = 'ev-choice-message';
    message.setAttribute('role', 'status');
    picker()?.prepend(message);
  }
  if (message) {
    const names = missing().map(group => group.querySelector('input')?.dataset.optionName).filter(Boolean);
    message.textContent = names.length ? `${config.dataset.missing} ${names.join(' / ')}` : pending ? config.dataset.loading : '';
    message.hidden = !message.textContent;
  }
}

function requestChoices(event) {
  if (!mobile.matches || (!missing().length && !pending)) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  prompted = true;
  sync();
  const target = missing()[0] || picker();
  if (target) {
    target.tabIndex = -1;
    target.focus({ preventScroll: true });
    target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
  }
}

if (section && sticky) {
  section.addEventListener('fabric:before-add', requestChoices);
  section.addEventListener('change', event => {
    if (!mobile.matches || !event.target.matches('variant-picker input[type="radio"]')) return;
    const input = event.target;
    const optionName = input.dataset.optionName || '';
    const colourGroup = groups().find(group => /^(color|colour|couleur)$/i.test(group.querySelector('input')?.dataset.optionName || ''));
    autoSelectOption = null;
    if (!automaticChange) {
      if (/^(color|colour|couleur)$/i.test(optionName)) autoSelectOption = 'size';
      else if (/^(size|taille)$/i.test(optionName) && colourGroup && !choices.has(colourGroup.dataset.fieldsetIndex)) autoSelectOption = 'colour';
    }
    choices.set(input.dataset.fieldsetIndex, input.value);
    pending = true;
    for (const group of groups()) {
      if (!group.querySelector('input:checked')) {
        const fallback = group.querySelector('input[checked]') || group.querySelector('input');
        if (fallback) fallback.checked = true;
      }
    }
  }, true);
  section.addEventListener(StandardEvents.productSelect, event => {
    if (!mobile.matches) { event.promise.then(updateAvailability).catch(() => {}); return; }
    const current = ++revision;
    const chooseFirstOption = autoSelectOption;
    event.promise.then(({ detail }) => {
      if (current !== revision) return;
      variant = detail?.resource || null;
      if (chooseFirstOption) {
        autoSelectOption = null;
        const optionPattern = chooseFirstOption === 'size' ? /^(size|taille)$/i : /^(color|colour|couleur)$/i;
        const optionGroup = groups().find(group => optionPattern.test(group.querySelector('input')?.dataset.optionName || ''));
        const availability = chooseFirstOption === 'colour' ? 'data-combination-available' : 'data-option-available';
        const firstAvailable = optionGroup?.querySelector(`input[${availability}="true"]`);
        if (firstAvailable) {
          choices.set(firstAvailable.dataset.fieldsetIndex, firstAvailable.value);
          if (!firstAvailable.checked) {
            firstAvailable.checked = true;
            automaticChange = true;
            try {
              firstAvailable.dispatchEvent(new Event('change', { bubbles: true }));
            } finally {
              automaticChange = false;
            }
            return;
          }
        } else if (optionGroup) {
          choices.delete(optionGroup.dataset.fieldsetIndex);
        }
      }
      pending = false;
      sync();
    }).catch(() => {
      if (current !== revision) return;
      pending = false;
      variant = null;
      sync();
    });
  });
  section.addEventListener('click', event => {
    if (event.target.closest('[ref="addToCartButton"]')) requestChoices(event);
  }, true);
  section.addEventListener('submit', event => {
    if (event.target.closest('product-form-component')) requestChoices(event);
  }, true);
  mobile.addEventListener('change', () => location.reload());
  sync();
}
